/** Searchable dropdown with filtering. Wraps React Aria ComboBox. */
import {
  ComboBox as AriaComboBox,
  Input,
  Button,
  ListBox,
  ListBoxItem,
  Popover,
  Label,
  Text,
  type ComboBoxProps as AriaComboBoxProps,
} from 'react-aria-components';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/foundation/utils/cn';

interface ComboBoxItem {
  id: string;
  label: string;
}

interface ComboBoxProps<T extends ComboBoxItem> extends Omit<AriaComboBoxProps<T>, 'children'> {
  label?: string;
  description?: string;
  errorMessage?: string;
  placeholder?: string;
  items: T[];
  className?: string;
  inputClassName?: string;
  children?: (item: T) => React.ReactNode;
}

export function ComboBox<T extends ComboBoxItem>({
  label,
  description,
  errorMessage,
  placeholder = 'Search...',
  items,
  className,
  inputClassName,
  children,
  ...props
}: ComboBoxProps<T>) {
  return (
    <AriaComboBox className={cn('flex flex-col gap-1', className)} {...props}>
      {label && (
        <Label className="block text-sm font-medium text-[var(--f-fg-secondary)]">{label}</Label>
      )}
      <div className="relative">
        <Input
          className={cn(
            'w-full border border-[var(--f-border-strong)] rounded-xl px-3 py-2 h-10 pr-9 box-border',
            'bg-[var(--f-bg-raised)] text-[var(--f-fg)]',
            'focus:outline-none focus:ring-2 focus:ring-[var(--f-accent)] focus:border-transparent',
            'transition-all',
            inputClassName
          )}
          placeholder={placeholder}
        />
        <Button className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-[var(--f-fg-muted)] hover:text-[var(--f-fg)] transition-colors cursor-pointer">
          <ChevronDown className="w-4 h-4" />
        </Button>
      </div>
      {description && (
        <Text slot="description" className="text-xs text-[var(--f-fg-muted)]">
          {description}
        </Text>
      )}
      {errorMessage && (
        <Text slot="errorMessage" className="text-xs text-[var(--f-error-text)]">
          {errorMessage}
        </Text>
      )}
      <Popover
        className={cn(
          'w-[var(--trigger-width)] overflow-auto rounded-xl border border-[var(--f-border-strong)]',
          'bg-[var(--f-bg-raised)] shadow-lg max-h-60'
        )}
      >
        <ListBox className="outline-none p-1" items={items}>
          {children ??
            ((item) => (
              <ListBoxItem
                key={item.id}
                id={item.id}
                className={cn(
                  'px-3 py-2 rounded-lg cursor-pointer outline-none text-sm',
                  'hover:bg-[var(--f-bg-sunken)] focus:bg-[var(--f-bg-sunken)]',
                  'data-[selected]:bg-[var(--f-accent-subtle)] data-[selected]:text-[var(--f-accent-text)]'
                )}
              >
                {item.label}
              </ListBoxItem>
            ))}
        </ListBox>
      </Popover>
    </AriaComboBox>
  );
}
